/* eslint-disable react-hooks/exhaustive-deps */
import get from "lodash/get";
import { useEffect, useState } from "react";
import { getFormattedDate } from "utils/dates";
import { Link } from "react-router-dom";
import moment from "moment";
import omit from "lodash/omit";
import { getuxvalue } from "handlers/ux";
import { convertAmountWithDecimal } from "utils";
import ListingTable from "components/ReactTable";
import { getLocaleString } from "utils/localization/locale";
import { getCreditMemoApplicationReports } from "./actions";
import FilterAndExport from "./FilterAndExport";

export const initialCreditMemoPaymentFilters = {
  from_date: moment(0).format("MM/DD/YYYY"),
  to_date: moment().format("MM/DD/YYYY"),
  interval: "all",
  sort_by: "application_date",
  sort_order: "desc",
  page: 1,
};

export default function CreditMemoApplication() {
  const [rows, setRows] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [filters, setFilters] = useState({
    ...initialCreditMemoPaymentFilters,
    ...getuxvalue("ANALYTICS_CREDIT_MEMO_APPLICATION_REPORT_FILTERS", {}),
  });

  const {
    page = 1,
    to_date,
    sort_by,
    from_date,
    sort_order,
    invoiceSelect,
    customerSelect,
    creditMemoSelect,
  } = filters;

  const [pagination, setPagination] = useState({
    pages: 1,
    next_num: null,
    prev_num: null,
  });

  const getData = async () => {
    setIsFetching(true);
    const updatedFilters = omit({ ...filters }, [
      "invoiceSelect",
      "customerSelect",
      "creditMemoSelect",
      "interval",
    ]);
    if (customerSelect) {
      updatedFilters.client_customer_rel_id = customerSelect.id;
    }
    if (invoiceSelect) {
      updatedFilters.invoice_number = invoiceSelect.name;
    }
    if (creditMemoSelect) {
      updatedFilters.credit_memo_number = creditMemoSelect.name;
    }
    const reportsData = await getCreditMemoApplicationReports(updatedFilters);
    setIsFetching(false);
    setRows(get(reportsData, "result", []));
    setPagination({
      pages: get(reportsData, "pages", 1),
      next_num: get(reportsData, "next_num", 0),
      prev_num: get(reportsData, "prev_num", 0),
    });
  };

  const onExport = async () => {
    const updatedFilters = omit({ ...filters, is_export: 1 }, [
      "invoiceSelect",
      "customerSelect",
      "creditMemoSelect",
      "interval",
      "page",
    ]);
    if (customerSelect) {
      updatedFilters.client_customer_rel_id = customerSelect.id;
    }
    if (invoiceSelect) {
      updatedFilters.invoice_number = invoiceSelect.name;
    }
    if (creditMemoSelect) {
      updatedFilters.credit_memo_number = creditMemoSelect.name;
    }
    const reportsData = await getCreditMemoApplicationReports(updatedFilters);
    return get(reportsData, "result", []);
  };

  const setSortBy = (key) =>
    setFilters({
      ...filters,
      sort_by: key,
      sort_order: key !== sort_by || sort_order === "asc" ? "desc" : "asc",
    });

  const setNextPage = () => setFilters({ ...filters, page: page + 1 });

  const setPage = (page) => setFilters({ ...filters, page });

  const setPrevPage = () => setFilters({ ...filters, page: page - 1 });

  useEffect(() => {
    getData();
  }, [
    page,
    sort_by,
    to_date,
    from_date,
    sort_order,
    invoiceSelect,
    customerSelect,
    creditMemoSelect,
  ]);

  const columns = [
    {
      header: () => getLocaleString("common.sr_no", "Sr. No."),
      show: true,
      accessorKey: "id",
      id: "id",
      className: "ps-3",
      size: 60,
      dragable: false,
      cell: (row) => (
        <div>
          {page === 1
            ? get(row, "row.index", 0) + 1
            : (page - 1) * 20 + get(row, "row.index", 0) + 1}
        </div>
      ),
    },
    {
      header: () => getLocaleString("common.customer", "Customer"),
      show: true,
      accessorKey: "invoice.client_customer_rel",
      id: "client_customer_rel",
      size: 150,
      dragable: false,
      cell: (data) => (
        <div>
          <Link to={`/customers/rel/${get(data.getValue(), "id", "")}`}>
            {get(data.getValue(), "name", "")}
          </Link>
        </div>
      ),
    },
    {
      header: () => getLocaleString("common.invoiceNumber", "Invoice Number"),
      show: true,
      accessorKey: "invoice",
      id: "invoice_number",
      size: 100,
      dragable: false,
      cell: (data) => (
        <div>
          <Link to={`/receivables/${get(data.getValue(), "id", "")}`}>
            {get(data.getValue(), "invoice_number", "")}
          </Link>
        </div>
      ),
    },
    {
      header: () => (
        <div
          className="cursor-pointer"
          onClick={() => setSortBy("invoice_amount")}
        >
          {getLocaleString("common.InvoiceAmount", "Invoice Amount")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      accessorKey: "invoice",
      id: "invoice_amount",
      headerClassName: "text-end",
      className: "text-end",
      size: 100,
      dragable: false,
      cell: (data) => (
        <div>
          {convertAmountWithDecimal(
            get(data.getValue(), "invoice_amount", 0),
            get(data.getValue(), "currency", "")
          )}
        </div>
      ),
    },
    {
      header: () => getLocaleString("common.credit_number", "Credit Number"),
      show: true,
      accessorKey: "credit_memo",
      id: "credit_memo_number",
      size: 100,
      dragable: false,
      cell: (data) => (
        <div>
          <div>
            <Link to={`/receivables/${get(data.getValue(), "id", "")}`}>
              {get(data.getValue(), "invoice_number", "")}
            </Link>
          </div>
        </div>
      ),
    },
    {
      header: () => (
        <div
        // className="cursor-pointer"
        // onClick={() => setSortBy("credit_memo_amount")}
        >
          {getLocaleString("common.credit_memo_amount", "Credit Memo Amount")}
          {/* <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up"></i>
            <i data-tlabel="sort list" className="fas fa-caret-down"></i>
          </span> */}
        </div>
      ),
      show: true,
      accessorKey: "credit_memo",
      id: "credit_memo_amount",
      headerClassName: "text-end",
      className: "text-end",
      size: 120,
      dragable: false,
      cell: (data) => (
        <div>
          {convertAmountWithDecimal(
            get(data.getValue(), "invoice_amount", 0),
            get(data.getValue(), "currency", "")
          )}
        </div>
      ),
    },
    {
      header: () => (
        <div
          className="cursor-pointer"
          onClick={() => setSortBy("application_date")}
        >
          {getLocaleString("creditapplication.applied_date", "Applied Date")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      accessorKey: "application_date",
      id: "application_date",
      size: 100,
      dragable: false,
      cell: (row) => <div>{getFormattedDate(row.getValue())}</div>,
    },
    {
      header: () => (
        <div className="cursor-pointer" onClick={() => setSortBy("amount")}>
          {getLocaleString("customer_details.applied_amount", "Applied Amount")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      accessorKey: "amount",
      id: "amount",
      headerClassName: "text-end",
      className: "text-end",
      size: 100,
      dragable: false,
      cell: (data) => (
        <div>
          {convertAmountWithDecimal(
            data.getValue(),
            get(data, "row.original.credit_memo.currency", "")
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="card-body">
      <div className="row">
        <FilterAndExport
          filters={filters}
          setFilters={setFilters}
          onExport={onExport}
        />
        <div className="col-md-12 p-0">
          <ListingTable
            data={rows}
            columns={columns}
            isFetching={isFetching}
            sorting={
              sort_by
                ? [
                    {
                      id: sort_by,
                      desc: sort_order === "desc",
                    },
                  ]
                : []
            }
            showPaginationSwitch
            currPage={page}
            setPage={setPage}
            totalPages={pagination.pages}
            nextPage={get(pagination, "next_num", "")}
            prevPage={get(pagination, "prev_num", "")}
            setNextPage={setNextPage}
            setPrevPage={setPrevPage}
          />
        </div>
      </div>
    </div>
  );
}
